import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, apiDownload, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, ErrorBox, formatDay, formatPartialDate, OUTCOME_LABELS } from '../components/common';
import { Icon } from '../components/icons';
import { config } from '../config';
import { formatDate } from '../labels';
import { ApplicationsListPage } from './Demandes';
import { NipReviewQueue } from './Nip';
import '../styles/delivrance.css';

interface Mention {
  inscriptionId: string;
  ruleId: string;
  decisionReference: string;
  decidedOn: string;
  jurisdiction: string;
  qualification: string;
  sentences: { nature?: string; durationMonths?: number; amountXaf?: number; suspended?: boolean }[];
}
interface Preparation {
  id: string;
  application_id: string;
  application_reference: string;
  outcome: 'mentions' | 'neant' | 'bloquee';
  result: {
    mentions: Mention[];
    excluded: { inscriptionId: string; ruleId: string }[];
    coverage: string;
    ruleSetVersion: string;
    ruleSetStatus: string;
  };
  blockers: string[];
  status: string;
  created_at: string;
  prepared_by_name: string;
  bulletin_type: string;
}
interface Issued {
  id: string;
  reference: string;
  bulletin_type: string;
  issued_at: string;
  valid_until: string | null;
  status: string;
  signature_scheme: string;
}
interface DeliveryInfo {
  issued: Issued[];
  bulletinType: string;
  identity?: { resolution: string | null; personId: string | null };
  paperSearch?: { requestedAt: string | null; attestedAt: string | null };
  preparations?: Preparation[];
}

const TYPE: Record<string, string> = { n1: 'Bulletin n°1', n2: 'Bulletin n°2', n3: 'Bulletin n°3' };
const ISSUED_STATUS: Record<string, { label: string; tone: string }> = {
  valide: { label: 'Valide', tone: 'ok' },
  revoque: { label: 'Révoqué', tone: 'danger' },
  remplace: { label: 'Remplacé', tone: 'muted' },
};
const COVERAGE: Record<string, string> = {
  complete_attestee: 'reprise complète attestée',
  recherche_papier_attestee: 'recherche papier attestée',
  insuffisante: 'insuffisante',
};

function InfoIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  );
}

function sentence(s: Mention['sentences'][number]) {
  return [
    s.nature,
    s.durationMonths !== undefined ? `${s.durationMonths} mois` : null,
    s.amountXaf !== undefined ? `${s.amountXaf.toLocaleString('fr-FR')} XAF` : null,
    s.suspended ? 'avec sursis' : null,
  ]
    .filter(Boolean)
    .join(', ');
}

export function PreparationView({ p }: { p: Preparation }) {
  return (
    <div className="prep">
      <div className="prep-meta">
        <Badge tone={p.outcome === 'bloquee' ? 'danger' : p.outcome === 'neant' ? 'info' : 'ok'}>
          {p.outcome === 'bloquee'
            ? 'Bloquée'
            : p.outcome === 'neant'
              ? 'Aucune mention'
              : `${p.result.mentions.length} mention(s)`}
        </Badge>
        <span className="small muted">
          {TYPE[p.bulletin_type]} - référentiel v{p.result.ruleSetVersion} - couverture :{' '}
          {COVERAGE[p.result.coverage] ?? p.result.coverage}
        </span>
      </div>
      {p.blockers.length > 0 && (
        <ul className="alert alert-danger prep-blockers">
          {p.blockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {p.result.mentions.length > 0 && (
        <ol className="prep-mentions">
          {p.result.mentions.map((m, i) => (
            <li key={m.inscriptionId}>
              <strong>
                {i + 1}. {m.jurisdiction} - {m.decisionReference} du {formatDay(m.decidedOn)}
              </strong>{' '}
              <span className="small muted">(règle {m.ruleId})</span>
              <div>{m.qualification}</div>
              {m.sentences.map((s, k) => (
                <div key={k} className="muted">
                  - {sentence(s)}
                </div>
              ))}
            </li>
          ))}
        </ol>
      )}
      {p.result.excluded.length > 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          {p.result.excluded.length} inscription(s) écartée(s) par les règles.
        </p>
      )}
    </div>
  );
}

interface Candidate {
  personId: string;
  score: number;
  surname: string | null;
  givenNames: string | null;
  birth: { year: number | null; month: number | null; day: number | null };
  birthPlace: string | null;
  reasons: string[];
  contradictions: string[];
}

/** Carte « Bulletin » sur une demande. */
export function DeliveryCard({
  applicationId,
  status,
  onChange,
}: {
  applicationId: string;
  status: string;
  onChange: () => void;
}) {
  const [info, setInfo] = useState<DeliveryInfo | null>(null);
  const [search, setSearch] = useState<{ outcome: string; candidates: Candidate[] } | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const load = useCallback(
    () =>
      api<DeliveryInfo>(`/delivrance/demandes/${applicationId}`)
        .then(setInfo)
        .catch(() => setInfo(null)),
    [applicationId],
  );
  useEffect(() => {
    void load();
  }, [load, status]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
      onChange();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!info) return null;
  const instructor = info.preparations !== undefined;
  if (!instructor && info.issued.length === 0) return null;
  const latest = info.preparations?.[0];

  return (
    <div className="card flow">
      <h2>Bulletin</h2>
      {info.issued.map((b) => (
        <div key={b.id} className="dl-issued">
          <span>
            {TYPE[b.bulletin_type]} <span className="mono">{b.reference}</span> - délivré le{' '}
            {formatDate(b.issued_at)}
            {b.valid_until ? `, valable jusqu’au ${formatDate(b.valid_until)}` : ''}
          </span>
          <Badge tone={ISSUED_STATUS[b.status]?.tone ?? 'muted'}>
            {ISSUED_STATUS[b.status]?.label ?? b.status}
          </Badge>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void apiDownload(`/bulletins/${b.id}/document`, `${b.reference}.pdf`)}
          >
            Télécharger (PDF)
          </button>
        </div>
      ))}
      <ErrorBox error={error} />
      {instructor && ['traitement_en_cours', 'verification_complementaire'].includes(status) && (
        <>
          <h3 className="dl-sub">Identité</h3>
          {info.identity?.resolution ? (
            <p className="alert alert-ok">
              {info.identity.resolution === 'personne_identifiee' ? (
                <>
                  Rattachée à la{' '}
                  <Link to={`/espace/registre/personnes/${info.identity.personId}`}>fiche du registre</Link>.
                </>
              ) : (
                'Aucune fiche au registre (constat motivé).'
              )}
            </p>
          ) : (
            <p className="alert alert-warn">
              Identité non résolue : recherchez la personne au registre puis rattachez la demande, ou
              constatez l’absence de fiche.
            </p>
          )}
          <div className="actions dl-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                void run(async () =>
                  setSearch(
                    await api(`/delivrance/demandes/${applicationId}/identite/recherche`, { method: 'POST' }),
                  ),
                )
              }
            >
              Rechercher au registre
            </button>
          </div>
          {search && (
            <div className="prep-box">
              <p style={{ margin: 0 }}>
                Résultat : <strong>{OUTCOME_LABELS[search.outcome]?.label ?? search.outcome}</strong>.{' '}
                <span className="muted">{OUTCOME_LABELS[search.outcome]?.help}</span>
              </p>
              <div className="field">
                <label htmlFor="id-note">Justification (pièces examinées)</label>
                <input id="id-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              {search.candidates.length > 0 && (
                <ul className="dl-cands">
                  {search.candidates.map((c) => (
                    <li key={c.personId}>
                      <span>
                        <strong>
                          {c.surname} {c.givenNames}
                        </strong>{' '}
                        - {formatPartialDate(c.birth)} {c.birthPlace ?? ''} - {Math.round(c.score * 100)} %
                      </span>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={note.trim().length < 10}
                        onClick={() =>
                          void run(() =>
                            api(`/delivrance/demandes/${applicationId}/identite`, {
                              method: 'POST',
                              body: { resolution: 'personne_identifiee', personId: c.personId, note },
                            }),
                          )
                        }
                      >
                        Rattacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="actions dl-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={note.trim().length < 10}
                  onClick={() =>
                    void run(() =>
                      api(`/delivrance/demandes/${applicationId}/identite`, {
                        method: 'POST',
                        body: { resolution: 'aucune_fiche', note },
                      }),
                    )
                  }
                >
                  Constater l’absence de fiche
                </button>
              </div>
            </div>
          )}
          <h3 className="dl-sub">Préparation</h3>
          {info.paperSearch?.requestedAt && !info.paperSearch.attestedAt && (
            <p className="alert alert-info">
              Recherche papier demandée le {formatDate(info.paperSearch.requestedAt)} : en attente
              d’attestation par le casier.
            </p>
          )}
          {status === 'traitement_en_cours' && (
            <div className="actions dl-actions">
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void run(() => api(`/delivrance/demandes/${applicationId}/preparer`, { method: 'POST' }))
                }
              >
                Contrôler et préparer
              </button>
              {!info.paperSearch?.requestedAt && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() =>
                    void run(() =>
                      api(`/delivrance/demandes/${applicationId}/recherche-papier`, { method: 'POST' }),
                    )
                  }
                >
                  Demander une recherche papier
                </button>
              )}
            </div>
          )}
          {latest && (
            <div className="prep-box">
              <div className="small muted">
                Préparation du {formatDate(latest.created_at)} par {latest.prepared_by_name} -{' '}
                {latest.status === 'prete'
                  ? 'prête : en attente de signature par une autre personne'
                  : latest.status === 'bloquee'
                    ? 'bloquée : un contrôle doit être levé avant la signature'
                    : latest.status === 'signee'
                      ? 'signée'
                      : latest.status.replace(/_/g, ' ')}
              </div>
              <PreparationView p={latest} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Espace Délivrance : file d'instruction et, pour les signataires, préparations à signer. */
export function DeliverySpacePage() {
  const { me } = useAuth();
  const canSign = me?.permissions.includes('bulletin.signer') ?? false;
  const canInstruct = me?.permissions.includes('bulletin.instruire') ?? false;
  return (
    <div className="stack">
      {canSign && <ToSign />}
      {canInstruct && <ApplicationsListPage mode="instruction" />}
      {canInstruct && <NipReviewQueue />}
    </div>
  );
}

function ToSign() {
  const [rows, setRows] = useState<Preparation[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState<{ reference: string } | null>(null);
  const load = () => api<Preparation[]>('/delivrance/a-signer').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const sign = async (id: string) => {
    setError(null);
    try {
      setDone(await api<{ reference: string }>(`/delivrance/preparations/${id}/signer`, { method: 'POST' }));
      await load();
    } catch (e) {
      setError(e as ApiError);
      await load();
    }
  };
  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Délivrance - à signer</h1>
          <p className="lead">
            Avant de signer, le système refait tous les contrôles : si un élément a changé depuis la
            préparation, la signature est refusée et un nouvel examen est demandé.
          </p>
        </div>
      </div>
      <ErrorBox error={error} />
      {done && (
        <p className="alert alert-ok" role="status">
          Bulletin {done.reference} signé et mis à disposition.
        </p>
      )}
      {rows?.length === 0 && (
        <section className="card">
          <div className="empty">
            <span className="ic">
              <Icon name="stamp" />
            </span>
            <p className="muted" style={{ margin: 0 }}>
              Aucune préparation en attente de signature.
            </p>
          </div>
        </section>
      )}
      {rows?.map((p) => (
        <section key={p.id} className="card flow sign-card">
          <div className="ch">
            <strong>
              Demande <Link to={`/espace/demandes/${p.application_id}`}>{p.application_reference}</Link>
            </strong>
            <span className="small muted">
              préparée par {p.prepared_by_name}, {formatDate(p.created_at)}
            </span>
          </div>
          <PreparationView p={p} />
          <div className="actions dl-actions">
            <button type="button" className="btn btn-gold" onClick={() => void sign(p.id)}>
              Autoriser et signer
            </button>
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------- Casier central : couverture, recherches papier, bulletins concernés ----------

export function CoveragePanel() {
  const [rows, setRows] = useState<
    { id: string; name: string; status: string | null; evidence: string | null; attested_at: string | null }[]
  >([]);
  const [form, setForm] = useState({
    jurisdictionId: '',
    status: 'partielle',
    periodFrom: '1960-01-01',
    evidence: '',
  });
  const [error, setError] = useState<ApiError | null>(null);
  const load = () => api<typeof rows>('/couverture').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      setRows(await api('/couverture', { method: 'POST', body: form }));
    } catch (err) {
      setError(err as ApiError);
    }
  };
  return (
    <div className="stack">
      <p className="alert alert-ic">
        <InfoIcon />
        <span>
          Sans reprise complète attestée du casier tenant, aucune absence de mention n’est conclue
          automatiquement : une recherche papier attestée est exigée.
        </span>
      </p>
      <div className="card np table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Casier</th>
              <th scope="col">Couverture</th>
              <th scope="col">Justification</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td data-l="Couverture">
                  <Badge
                    tone={
                      r.status === 'complete_attestee' ? 'ok' : r.status === 'partielle' ? 'warn' : 'muted'
                    }
                  >
                    {r.status === 'complete_attestee'
                      ? 'Complète attestée'
                      : r.status === 'partielle'
                        ? 'Partielle'
                        : r.status === 'non_reprise'
                          ? 'Non reprise'
                          : 'Non renseignée'}
                  </Badge>
                </td>
                <td data-l="Justification">
                  <span className="dl-cell small">
                    {r.evidence ?? '-'}
                    {r.attested_at && <span className="muted">{formatDate(r.attested_at)}</span>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form className="card flow" onSubmit={submit} aria-label="Attester la couverture d’un casier">
        <div className="fg">
          <div className="field">
            <label htmlFor="cv-j">Casier</label>
            <select
              id="cv-j"
              required
              value={form.jurisdictionId}
              onChange={(e) => setForm({ ...form, jurisdictionId: e.target.value })}
            >
              <option value="">- Choisir -</option>
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cv-s">État</label>
            <select
              id="cv-s"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="non_reprise">Non reprise</option>
              <option value="partielle">Partielle</option>
              <option value="complete_attestee">Complète attestée</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="cv-e">Justification</label>
            <input
              id="cv-e"
              required
              minLength={5}
              value={form.evidence}
              onChange={(e) => setForm({ ...form, evidence: e.target.value })}
            />
          </div>
          <div className="field end">
            <button className="btn" type="submit">
              Attester
            </button>
          </div>
        </div>
      </form>
      <ErrorBox error={error} />
    </div>
  );
}

export function PaperSearchPanel() {
  const [rows, setRows] = useState<
    {
      id: string;
      reference: string;
      declared_identity: { surname?: string; givenNames?: string };
      paper_search_requested_at: string;
      jurisdiction: string | null;
    }[]
  >([]);
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const load = () => api<typeof rows>('/delivrance/recherches-papier').then(setRows).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const attest = async (id: string) => {
    try {
      await api(`/delivrance/recherches-papier/${id}/attester`, {
        method: 'POST',
        body: { reference: refs[id] ?? '' },
      });
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <div className="stack">
      <p className="lead" style={{ marginTop: 0, fontSize: 15.5 }}>
        Recherches documentaires demandées par la délivrance, à faire dans les registres papier puis à
        attester.
      </p>
      <ErrorBox error={error} />
      {rows.length === 0 && (
        <section className="card">
          <div className="empty">
            <span className="ic">
              <Icon name="registry" />
            </span>
            <p className="muted" style={{ margin: 0 }}>
              Aucune recherche en attente.
            </p>
          </div>
        </section>
      )}
      {rows.map((r) => (
        <div key={r.id} className="card paper-row">
          <span>
            <span className="mono">{r.reference}</span> - {r.declared_identity.surname}{' '}
            {r.declared_identity.givenNames} - {r.jurisdiction} - demandée le{' '}
            {formatDate(r.paper_search_requested_at)}
          </span>
          <input
            aria-label="Registres consultés"
            placeholder="Registres consultés"
            value={refs[r.id] ?? ''}
            onChange={(e) => setRefs({ ...refs, [r.id]: e.target.value })}
          />
          <button
            type="button"
            className="btn"
            disabled={(refs[r.id] ?? '').length < 5}
            onClick={() => void attest(r.id)}
          >
            Attester
          </button>
        </div>
      ))}
    </div>
  );
}

export function AffectedPanel() {
  const [rows, setRows] = useState<
    { id: string; reference: string; issued_at: string; application_reference: string; motif: string }[]
  >([]);
  useEffect(() => {
    api<typeof rows>('/bulletins/concernes')
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  const MOTIF: Record<string, string> = {
    evenement_juridique: 'Événement juridique validé après la délivrance',
    inscription_modifiee: 'Inscription corrigée après la délivrance',
    identite_modifiee: 'Fusion ou séparation après la délivrance',
  };
  return (
    <div className="stack">
      <p className="alert alert-ic">
        <InfoIcon />
        <span>
          Une mise à jour ne révoque pas automatiquement un bulletin : l’autorité décide des suites.
        </span>
      </p>
      {rows.length === 0 ? (
        <section className="card">
          <div className="empty">
            <span className="ic">
              <Icon name="file" />
            </span>
            <p className="muted" style={{ margin: 0 }}>
              Aucun bulletin concerné.
            </p>
          </div>
        </section>
      ) : (
        <section className="card">
          <ul className="affected">
            {rows.map((r) => (
              <li key={r.id}>
                <span className="mono">{r.reference}</span> (demande {r.application_reference}, délivré le{' '}
                {formatDate(r.issued_at)}) - {MOTIF[r.motif] ?? r.motif}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------- Vérification publique ----------

interface VerifyResult {
  resultat: 'reconnu' | 'inconnu' | 'non_conforme';
  etat?: string;
  reference?: string;
  type?: string;
  emisLe?: string;
  valableJusquau?: string | null;
  autorite?: string;
  avertissement?: string;
  detail?: string;
  conformeALEmpreinte?: boolean;
  signatureVerifiee?: boolean;
}

const ETAT: Record<string, { label: string; tone: string }> = {
  valide: { label: 'Valide', tone: 'ok' },
  expire: { label: 'Expiré', tone: 'warn' },
  revoque: { label: 'Révoqué', tone: 'danger' },
  remplace: { label: 'Remplacé par un document plus récent', tone: 'warn' },
};

export function VerifyPage() {
  const { token } = useParams();
  const { me } = useAuth();
  const [detail, setDetail] = useState<(VerifyResult & { mentions: Mention[] }) | null>(null);
  const [code, setCode] = useState(token ?? '');
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [fileResult, setFileResult] = useState<VerifyResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const check = useCallback(async (c: string) => {
    setError(null);
    try {
      setResult(await api<VerifyResult>(`/verification/${encodeURIComponent(c.trim())}`));
    } catch (e) {
      setError(e as ApiError);
    }
  }, []);
  useEffect(() => {
    if (token) void check(token);
  }, [token, check]);

  const checkFile = async (file: File) => {
    setError(null);
    const form = new FormData();
    form.append('fichier', file);
    try {
      const res = await fetch(`${config.apiUrl}/verification/fichier`, { method: 'POST', body: form });
      setFileResult((await res.json()) as VerifyResult);
    } catch {
      setError(new ApiError(0, undefined, 'Vérification impossible.'));
    }
  };

  const Panel = ({ r }: { r: VerifyResult }) =>
    r.resultat === 'reconnu' ? (
      <section className="card flow verify-result" aria-live="polite">
        <div className="ch">
          <strong>Document reconnu</strong>
          <div className="actions">
            <Badge tone={ETAT[r.etat ?? '']?.tone ?? 'muted'}>{ETAT[r.etat ?? '']?.label ?? r.etat}</Badge>
            {r.conformeALEmpreinte && <Badge tone="ok">Fichier conforme à l’original signé</Badge>}
          </div>
        </div>
        <dl className="details">
          <dt>Type</dt>
          <dd>{r.type}</dd>
          <dt>Référence</dt>
          <dd>
            <span className="mono">{r.reference}</span>
          </dd>
          <dt>Émis le</dt>
          <dd>{r.emisLe ? formatDate(r.emisLe) : '-'}</dd>
          {r.valableJusquau && (
            <>
              <dt>Valable jusqu’au</dt>
              <dd>{formatDate(r.valableJusquau)}</dd>
            </>
          )}
          <dt>Autorité</dt>
          <dd>{r.autorite}</dd>
        </dl>
        {r.avertissement && <p className="alert">{r.avertissement}</p>}
      </section>
    ) : (
      <p className="alert alert-danger" role="alert" style={{ overflowWrap: 'anywhere' }}>
        {r.resultat === 'inconnu' ? 'Code inconnu : ce document n’a pas été émis par CASIJUD.' : r.detail}
      </p>
    );

  return (
    <div className="verify">
      <div className="head">
        <div>
          <h1>Vérifier un document</h1>
          <p className="lead">
            La vérification indique si un bulletin est reconnu et son état. Elle n’affiche{' '}
            <strong>jamais</strong> l’identité ni les mentions.
          </p>
        </div>
      </div>
      <form
        className="card flow"
        onSubmit={(e) => {
          e.preventDefault();
          void check(code);
        }}
      >
        <div className="field">
          <label htmlFor="code" className="verify-label">
            Code de vérification (sous le QR code)
          </label>
          <input
            id="code"
            className="verify-code"
            autoComplete="off"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div className="actions">
          <button className="btn" type="submit" disabled={code.trim().length < 20}>
            Vérifier le code
          </button>
        </div>
      </form>
      <ErrorBox error={error} />
      {result && <Panel r={result} />}
      <div className="verify-or" aria-hidden="true">
        ou
      </div>
      <section className="card flow">
        <div className="field">
          <label htmlFor="file" className="verify-h">
            Contrôler un fichier PDF reçu
          </label>
          <div className="drop">
            <span className="drop-ic">
              <Icon name="file" />
            </span>
            <span className="drop-t" aria-hidden="true">
              {fileName ?? (
                <>
                  Déposez le PDF ici ou <span className="clay">choisissez un fichier</span>
                </>
              )}
            </span>
            <input
              id="file"
              type="file"
              accept="application/pdf"
              aria-describedby="file-hint"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFileName(f?.name ?? null);
                if (f) void checkFile(f);
              }}
            />
          </div>
          <span className="hint" id="file-hint">
            Le fichier n’est pas conservé : seule son empreinte est comparée à celle du document signé.
          </span>
        </div>
      </section>
      {fileResult && <Panel r={fileResult} />}
      <p className="alert alert-ic">
        <InfoIcon />
        <span>
          Une copie papier peut avoir été modifiée : pour une garantie complète, contrôlez le fichier PDF
          lui-même.
        </span>
      </p>
      {me?.permissions.includes('organisme.demander') && result?.resultat === 'reconnu' && (
        <section className="card flow">
          <h2>Contrôle détaillé (destinataire)</h2>
          <p className="small muted" style={{ margin: 0 }}>
            Réservé à l’organisme destinataire de ce bulletin : la consultation est tracée.
          </p>
          <div className="actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                void api<VerifyResult & { mentions: Mention[] }>(
                  `/verification/${encodeURIComponent(code.trim())}/detail`,
                )
                  .then(setDetail)
                  .catch((e: ApiError) => setError(e))
              }
            >
              Afficher les mentions
            </button>
          </div>
          {detail && (
            <ul className="prep-mentions">
              {detail.mentions.length === 0 && <li>Aucune mention.</li>}
              {detail.mentions.map((m) => (
                <li key={m.inscriptionId}>
                  {m.jurisdiction} - {m.decisionReference} du {formatDay(m.decidedOn)} : {m.qualification}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
